import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, IconButton, Typography } from "@mui/material";
import disclosureInfoIcon from "../assets/estimated-impressions-info.svg";
import disclosureCloseIcon from "../assets/estimated-impressions-close.svg";

import type { EstimatedImpressionsResult } from "@/features/campaigns/shared/helpers/estimatedImpressionsHelper";

export const ESTIMATED_IMPRESSIONS_DISCLOSURE =
  "Estimated impressions are estimates only and may vary from actual results.";
const ESTIMATED_IMPRESSIONS_PREFIX = "Est. Impressions:";

type EstimatedImpressionsTextProps = {
  enabled?: boolean;
  estimate?: EstimatedImpressionsResult | null;
  delayMs?: number;
};

type EstimatedImpressionsDisclosureProps = {
  enabled?: boolean;
  className?: string;
  variant?: "inline" | "standalone";
};

// Scope registration to the form/table containing the disclosure.
const VisibilityContext = createContext<{
  hasVisibleEstimate: boolean;
  register: (_id: string) => () => void;
}>({ hasVisibleEstimate: false, register: () => () => {} });

export function EstimatedImpressionsVisibilityProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [visibleIds, setVisibleIds] = useState<Set<string>>(() => new Set());
  const register = useCallback((id: string) => {
    setVisibleIds((previous) => new Set(previous).add(id));
    return () =>
      setVisibleIds((previous) => {
        const next = new Set(previous);
        next.delete(id);
        return next;
      });
  }, []);
  const value = useMemo(
    () => ({ hasVisibleEstimate: visibleIds.size > 0, register }),
    [visibleIds, register]
  );
  return (
    <VisibilityContext.Provider value={value}>
      {children}
    </VisibilityContext.Provider>
  );
}

export function EstimatedImpressionsText({
  enabled = false,
  estimate,
  delayMs = 1000,
}: EstimatedImpressionsTextProps) {
  const [visibleText, setVisibleText] = useState<string | null>(null);
  const nextText = estimate?.displayText || null;
  const { register } = useContext(VisibilityContext);
  const id = useId();
  const isDisplayed = Boolean(
    enabled &&
      !estimate?.unavailableReason &&
      visibleText &&
      visibleText === nextText
  );
  useLayoutEffect(() => {
    if (isDisplayed) return register(id);
    return undefined;
  }, [id, isDisplayed, register]);

  useEffect(() => {
    setVisibleText(null);

    if (!enabled || !nextText) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setVisibleText(nextText);
    }, delayMs);

    return () => window.clearTimeout(timeoutId);
  }, [delayMs, enabled, nextText]);

  if (!enabled || estimate?.unavailableReason) {
    return null;
  }

  if (isDisplayed && visibleText) {
    const impressionRange = visibleText.startsWith(ESTIMATED_IMPRESSIONS_PREFIX)
      ? visibleText.slice(ESTIMATED_IMPRESSIONS_PREFIX.length).trim()
      : "";

    return (
      <Typography
        variant="caption"
        className="estimated-impressions-text d-block mt-1"
        sx={{
          color: "#606060",
          fontFamily: "Open Sans, sans-serif",
          fontSize: "12px",
          fontWeight: 400,
          lineHeight: "19.92px",
        }}
      >
        {impressionRange ? (
          <>
            {ESTIMATED_IMPRESSIONS_PREFIX}{" "}
            <Box
              component="span"
              sx={{
                color: "inherit",
                fontFamily: "Open Sans, sans-serif",
                fontWeight: 700,
              }}
            >
              {impressionRange}
            </Box>
          </>
        ) : (
          visibleText
        )}
      </Typography>
    );
  }
  return null;
}

export function EstimatedImpressionsDisclosure({
  enabled = false,
  className,
  variant = "inline",
}: EstimatedImpressionsDisclosureProps) {
  const [isVisible, setIsVisible] = useState(true);

  const { hasVisibleEstimate } = useContext(VisibilityContext);

  if (!enabled || !isVisible || !hasVisibleEstimate) {
    return null;
  }

  return (
    <Box
      className={["estimated-impressions-disclosure", className || ""]
        .filter(Boolean)
        .join(" ")}
      data-testid="estimated-impressions-disclosure"
      sx={{
        alignItems: "center",
        bgcolor: "rgba(255, 229, 173, 0.34)",
        border: "1px solid rgba(255, 229, 173, 0.34)",
        borderRadius: "var(--radius-container)",
        color: "#714d00",
        display: "flex",
        fontFamily: "Open Sans, sans-serif",
        fontSize: "11.2px",
        gap: "10px",
        lineHeight: "16.8px",
        mt: variant === "standalone" ? 2 : 1,
        p: "10px",
        wordBreak: "break-word",
      }}
    >
      {variant === "standalone" ? (
        <Box
          component="img"
          src={disclosureInfoIcon}
          alt=""
          sx={{ flex: "0 0 auto" }}
        />
      ) : (
        <InfoOutlinedIcon sx={{ fontSize: 16, flex: "0 0 auto" }} />
      )}
      <Typography
        variant="caption"
        sx={{
          color: "inherit",
          flex: 1,
          fontFamily: "Open Sans, sans-serif",
          fontSize: "11.2px",
          fontWeight: 400,
          lineHeight: "16.8px",
          minWidth: 0,
        }}
      >
        {ESTIMATED_IMPRESSIONS_DISCLOSURE}
      </Typography>
      <IconButton
        aria-label="Dismiss estimated impressions disclosure"
        onClick={() => setIsVisible(false)}
        size="small"
        sx={{ color: "inherit", flex: "0 0 auto", p: 0 }}
      >
        {variant === "standalone" ? (
          <Box component="img" src={disclosureCloseIcon} alt="" />
        ) : (
          <CloseIcon sx={{ fontSize: 14.5 }} />
        )}
      </IconButton>
    </Box>
  );
}
